import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Heart, MessageCircle, MessageSquare, ArrowLeft, CheckCheck, Luggage, Star, MapPin, Trash2,
} from "lucide-react";
import { api } from "@/lib/api";
import { SectionRibbon } from "@/components/AppShell";
import { defaultAvatarUrl } from "@/lib/avatar";
import { NOTIF } from "@/constants/testIds";
import { toast } from "sonner";
import ClassifyBookingDialog from "@/components/ClassifyBookingDialog";

function timeAgo(iso) {
  if (!iso) return "just now";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

const ICONS = {
  like: { Icon: Heart, color: "text-tb-coral", bg: "bg-tb-coral-soft" },
  comment: { Icon: MessageCircle, color: "text-tb-blue", bg: "bg-tb-blue-soft" },
  dm: { Icon: MessageSquare, color: "text-tb-purple", bg: "bg-tb-purple-soft" },
  booking_to_classify: { Icon: Luggage, color: "text-tb-gold", bg: "bg-tb-gold/20" },
  advisor_booking_created: { Icon: Luggage, color: "text-tb-navy", bg: "bg-tb-gold/25" },
  story_promoted: { Icon: Star, color: "text-tb-gold", bg: "bg-tb-gold/20" },
};

export default function NotificationsPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
    } catch (err) {
      console.error("[NotificationsPage] load failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // 2026-07-17 — dispatch a lightweight window event whenever
  // notifications state changes, so the header's <PageHeader> bell
  // badge refetches its unread count immediately (previously it
  // waited up to 30s for the next poll to fire, leaving a stale red
  // badge for minutes after "Mark all read").
  const _pingBadgeBell = () => {
    try {
      window.dispatchEvent(new CustomEvent("travelbio:notifications-changed"));
    } catch { /* no-op */ }
  };

  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try {
      await api.post(`/notifications/${id}/read`);
      _pingBadgeBell();
    }
    catch (err) { console.error("[NotificationsPage] markRead failed:", err); }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await api.post("/notifications/read-all");
      _pingBadgeBell();
      toast.success("all caught up ✨");
    } catch (err) {
      console.error("[NotificationsPage] markAllRead failed:", err);
    }
  };

  const clearAll = async () => {
    if (items.length === 0) return;
    if (!window.confirm("Delete all notifications? This can't be undone.")) return;
    const prev = items;
    // Optimistic: empty the list; roll back on failure.
    setItems([]);
    try {
      await api.delete("/notifications");
      _pingBadgeBell();
      toast.success("notifications cleared");
    } catch (err) {
      console.error("[NotificationsPage] clearAll failed:", err);
      setItems(prev);
      toast.error("couldn't clear notifications — try again");
    }
  };

  const [classifyTrip, setClassifyTrip] = useState(null);

  const goToPost = (n) => {
    // Bug 10: universal `n.link` deep-link — new notification types
    // (destination_request, user_created_destination, plus any future
    // ones) set a `link` field on the row; we honour it before the
    // type-specific handlers below.
    if (n.link) {
      if (!n.read) markRead(n.id);
      nav(n.link);
      return;
    }
    if (n.type === "booking_to_classify") {
      if (!n.read) markRead(n.id);
      if (n.classified_trip_id) {
        // Already filed — jump to the trip detail.
        nav(`/booked/${n.classified_trip_id}`);
      } else {
        // Open the classify dialog inline. On submit we file the trip via
        // POST /booked-trips/from-booking.
        setClassifyTrip({
          notification_id: n.id,
          destination_name: n.destination_name || n.metadata?.destination_name,
          country: n.country || n.metadata?.country,
          image: n.image,
        });
      }
      return;
    }
    if (n.type === "story_promoted") {
      if (!n.read) markRead(n.id);
      if (n.year) nav(`/chapters/${n.year}`);
      return;
    }
    if (n.type === "dm") {
      if (!n.read) markRead(n.id);
      if (n.from_user_id) nav(`/messages?with=${n.from_user_id}`);
      else nav("/messages");
      return;
    }
    if (!n.read) markRead(n.id);
    if (n.post_id) nav("/lounge");
  };

  const unreadCount = items.filter((n) => !n.read).length;

  return (
    <div data-testid={NOTIF.page} className="flex flex-col">
      <SectionRibbon>your notifications</SectionRibbon>

      <div className="px-5 pt-3 pb-2 flex items-center justify-between">
        <button
          onClick={() => nav(-1)}
          aria-label="Back"
          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border-2 border-tb-navy/15 font-hand text-tb-navy tb-tap text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> back
        </button>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              data-testid={NOTIF.markAllRead}
              onClick={markAllRead}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-sm tb-tap"
            >
              <CheckCheck className="w-4 h-4" /> mark all read
            </button>
          )}
          {items.length > 0 && (
            <button
              data-testid={NOTIF.clearAll}
              onClick={clearAll}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border-2 border-tb-navy/15 font-hand text-tb-navy tb-tap text-sm"
            >
              <Trash2 className="w-4 h-4" /> clear all
            </button>
          )}
        </div>
      </div>

      <div className="px-5 pb-8">
        {loading ? (
          <div className="font-hand text-tb-navy/60 text-xl text-center py-12">
            ~ checking your trail ~
          </div>
        ) : items.length === 0 ? (
          <div data-testid={NOTIF.empty} className="text-center py-16">
            <p className="font-serif-it text-tb-navy text-2xl">no notifications yet</p>
            <p className="font-hand text-tb-navy/60 mt-2">
              when other travelers like your stories or leave comments, they'll appear here.
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {items.map((n) => {
              const meta = ICONS[n.type] || ICONS.comment;
              const Icon = meta.Icon;
              const isBooking = n.type === "booking_to_classify";
              const isFiled = isBooking && !!n.classified_trip_id;
              const avatarSrc = isBooking
                ? (n.image || n.from_avatar || "")
                : (n.from_avatar || defaultAvatarUrl(n.id));
              return (
                <li key={n.id}>
                  <button
                    data-testid={NOTIF.item(n.id)}
                    onClick={() => goToPost(n)}
                    className={
                      "w-full text-left rounded-2xl border-2 px-3 py-2.5 flex items-center gap-3 tb-tap transition-colors " +
                      (n.read
                        ? "bg-white border-tb-navy/15"
                        : "bg-white border-tb-gold/60 shadow-sm")
                    }
                  >
                    <div className="relative shrink-0">
                      {avatarSrc ? (
                        <img
                          src={avatarSrc}
                          alt=""
                          className={
                            isBooking
                              ? "w-12 h-12 rounded-xl bg-tb-cream-3 object-cover"
                              : "w-11 h-11 rounded-full bg-tb-cream-3 object-cover"
                          }
                        />
                      ) : (
                        <div className="w-11 h-11 rounded-full bg-tb-cream-3" />
                      )}
                      <span className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full ${meta.bg} ${meta.color} border-2 border-tb-cream flex items-center justify-center`}>
                        <Icon className="w-3 h-3" fill={n.type === "like" ? "currentColor" : "none"} strokeWidth={2} />
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-ui text-sm text-tb-navy/90 leading-snug">
                        {isBooking
                          ? (isFiled
                              ? `Filed: ${n.destination_name}`
                              : `Where should we file your ${n.destination_name} booking?`)
                          : n.message}
                      </div>
                      {isBooking && !isFiled && (
                        <div className="font-hand text-xs text-tb-navy/60 mt-0.5 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {n.country || "—"}
                        </div>
                      )}
                      <div className="font-hand text-xs text-tb-navy/55 mt-0.5">
                        {timeAgo(n.created_at)} ago
                      </div>
                    </div>
                    {isBooking && !isFiled ? (
                      <span
                        data-testid={`notif-file-${n.id}`}
                        className="shrink-0 px-3 py-1.5 rounded-full bg-tb-coral text-white font-hand text-xs"
                      >
                        file it →
                      </span>
                    ) : !n.read ? (
                      <span className="w-2 h-2 rounded-full bg-tb-coral shrink-0" aria-label="unread" />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <ClassifyBookingDialog
        open={!!classifyTrip}
        trip={classifyTrip}
        onClose={() => setClassifyTrip(null)}
        onClassified={() => {
          // Optimistically remove the classified notif; server marks it filed.
          if (classifyTrip?.notification_id) {
            setItems((prev) => prev.filter((n) => n.id !== classifyTrip.notification_id));
          }
          load();
        }}
      />
    </div>
  );
}
